// My Characters' raw SELECT rows -> the shared catalog table. No server-built cells or controls.
import { decodeHtmlEntitiesForPlainText } from '../shared/decode-html-entities';
import { playerCharacterTotalLevel, type PlayerCharacterClassSlot, type PlayerCharacterListPicture, type PlayerCharacterListRow } from '../shared/player-character-list-row';
import { compilePictureRow } from '../janus-engine/compile/picture-row-construction';
import type { PicturesBucket } from '../thumbnail/core';
import { resolveUrl } from './catalog-list';
import { catalogIcon } from './dom-helpers';
import type { CatalogTableResponse, CellSegment, TableCell, TableColumn, TableRow } from './types';

export const PLAYER_CHARACTER_LIST_SCHEMA = 'player-character-list-v1';

export interface PlayerCharacterListResponse {
    schema: typeof PLAYER_CHARACTER_LIST_SCHEMA;
    group: 'campaign' | 'unattached';
    characters: PlayerCharacterListRow[];
    total: number;
    cdn_url: string;
    csrf_token: string;
}

interface CharacterActions {
    copyToClipboard: (element: string) => void;
    showCopiedTooltip: (id: string) => void;
    ftTryDeleteCreature22?: (id: string, deletePlayerCharacter: boolean) => Promise<'handled' | 'refused'>;
}

const icon = (id: string): CellSegment => ({ kind: 'icon', id });
const plain = decodeHtmlEntitiesForPlainText;

function columns(group: PlayerCharacterListResponse['group']): TableColumn[] {
    const column = (key: string, header: CellSegment[], label: string, sortable: boolean, filterable: boolean,
        sortType: 'text' | 'num', align: TableColumn['align'], width: string): TableColumn => ({
        key, header_segments: header, header_label: label, sortable, filterable, sort_type: sortType, align, width,
    });
    return [
        column('picture', [icon('picture')], 'Picture', false, false, 'text', 'center', '100px'),
        column('name', [icon('player_character')], 'Character', true, true, 'text', 'left', '16rem'),
        ...(group === 'campaign' ? [column('campaign', [icon('campaign')], 'Campaign', true, true, 'text', 'left', '14rem')] : []),
        column('level', [icon('level')], 'Level', true, false, 'num', 'center', '60px'),
        column('class', [icon('class'), { kind: 'text', value: ' | ' }, icon('subclass')], 'Class | Subclass', true, true, 'text', 'center', '150px'),
        column('settings', [icon('settings')], 'Settings', false, false, 'text', 'center', '15rem'),
    ];
}

function aggregate<T>(value: string | T[] | null): T[] {
    if (value === null) return [];
    const rows: unknown = typeof value === 'string' ? JSON.parse(value) : value;
    if (!Array.isArray(rows)) throw new Error('catalog-player-character-aggregate');
    return rows as T[];
}

/** SQL JSON_ARRAYAGG has no ordering promise. Reproduce fetch_pictures' complete order here. */
function portraits(row: PlayerCharacterListRow, cdnUrl: string): PicturesBucket {
    const rows = aggregate<PlayerCharacterListPicture>(row.pictures_json);
    // SQL sorts NULL before non-NULL ascending (and after them descending).
    const ascending = (a: number | string | null, b: number | string | null): number =>
        a === null ? (b === null ? 0 : -1) : b === null ? 1 : Number(a) - Number(b);
    rows.sort((a, b) => ascending(b.primary_picture, a.primary_picture)
        || ascending(b.picture_thumbnail, a.picture_thumbnail)
        || ascending(a.picture_number, b.picture_number) || ascending(a.picture_i, b.picture_i));
    const pictures: PicturesBucket = {};
    for (const picture of rows) {
        // Shared raw-picture projection preserves reference copies' storage folder as well as
        // the normal PC/actor folders. Thumbnail's actor -> PC cascade remains unchanged.
        const projected = compilePictureRow(picture, { cdnUrl }) as Record<string, unknown>;
        // A missing upload number stays missing — defaulting it to 0 would pair unrelated uploads.
        const uploadNumber = picture.picture_number;
        (pictures[picture.picture_attached_item_type] ??= []).push({
            picture_url: String(projected.picture_url ?? ''),
            ...(uploadNumber == null || uploadNumber === '' ? {} : { picture_number: Number(uploadNumber) }),
            ...(Number(picture.primary_picture ?? 0) === 1 ? { primary_picture: 1 } : {}),
            ...(Number(picture.picture_thumbnail ?? 0) === 1 ? { picture_thumbnail: 1 } : {}),
        });
    }
    return pictures;
}

function nameCell(name: string, href: string): TableCell {
    return { segments: [{ kind: 'text', value: name, link: resolveUrl(href), cls: 'link-primary d-block text-truncate' }],
        sort: name, text: name.toLowerCase() };
}

function classCell(slots: PlayerCharacterClassSlot[]): TableCell {
    const segments: CellSegment[] = [];
    const names: string[] = [];
    for (const slot of slots) {
        if (!slot.level || slot.level === '0') continue;
        const subclass = String(slot.subclass_id ?? '');
        const id = subclass || String(slot.class_id ?? '');
        if (!id) continue;
        const name = plain(slot.item_name);
        if (name) names.push(name);
        segments.push({ kind: 'icon', id, w: 50, h: 50, item_type: subclass ? 'subclass' : 'class', title: name,
            link: resolveUrl(`item.php?id=${encodeURIComponent(id)}`), target: '_blank', badge: String(slot.level) });
    }
    const text = names.join(' ').trim();
    return { segments, sort: text, text: text.toLowerCase() };
}

/** The old settings icons are 25px (table header icons keep the renderer's default size). */
function settingIcon(parent: HTMLElement, tag: 'a' | 'button', className: string, iconId: string, label: string): HTMLElement {
    const node = document.createElement(tag);
    node.className = className;
    node.setAttribute('aria-label', label);
    node.innerHTML = catalogIcon(iconId, 25, 25, label);
    parent.appendChild(node);
    return node;
}

function appendIconAction(parent: HTMLElement, className: string, iconId: string, label: string, fn: () => void | Promise<void>): HTMLButtonElement {
    const button = settingIcon(parent, 'button', className, iconId, label) as HTMLButtonElement;
    button.type = 'button';
    button.addEventListener('click', () => { void fn(); });
    return button;
}

function appendIconLink(parent: HTMLElement, href: string, className: string, iconId: string, label: string): void {
    (settingIcon(parent, 'a', className, iconId, label) as HTMLAnchorElement).href = href;
}

/** Same existing handlers, endpoint payloads and DOM ids as the former PHP settings cluster. */
function settingsNode(row: PlayerCharacterListRow, csrfToken: string): Node {
    const id = row.encounter_creature_id ?? row.item_id;
    const encoded = encodeURIComponent(id);
    const actions = window as unknown as CharacterActions;
    const root = document.createElement('div');
    const wrap = document.createElement('div');
    wrap.className = 'd-flex flex-column align-items-center gap-1 w-100';
    root.appendChild(wrap);
    const line = (): HTMLDivElement => {
        const node = document.createElement('div');
        node.className = 'd-flex flex-wrap justify-content-center align-items-center gap-1';
        wrap.appendChild(node);
        return node;
    };
    const first = line();
    appendIconLink(first, resolveUrl(`tools/player_characters/create_player_character.php?id=${encoded}`),
        'btn btn-sm btn-info', 'edit_player_character', 'Edit character');
    const share = appendIconAction(first, 'btn btn-sm btn-dark border', 'share_player_character', 'Share character', () => {
        actions.copyToClipboard(`#hidden_share_pc_${id}`);
        actions.showCopiedTooltip(`btn_share_pc_list_${id}`);
    });
    share.id = `btn_share_pc_list_${id}`;
    appendIconLink(first, resolveUrl(`tools/media/media_manager.php?type=player_character&id=${encoded}`),
        'btn btn-sm btn-light', 'modify_picture_gallery', 'Media Manager');
    // Training Grounds reads the saved PC row by item_id; the page itself shows the in-combat / session gate.
    appendIconLink(first, resolveUrl(`training_grounds.php?player_character_id=${encodeURIComponent(row.item_id)}`),
        'btn btn-sm btn-success', 'training_grounds', 'Training Grounds');

    const second = line();
    const pair = (parent: HTMLElement, state: number, ids: [string, string], icons: [string, string],
        classes: [string, string], labels: [string, string], update: (value: 0 | 1) => Promise<unknown>, wait = false): void => {
        const show = (value: number): void => {
            off.style.display = value === 0 ? '' : 'none';
            on.style.display = value === 1 ? '' : 'none';
        };
        const change = async (value: 0 | 1): Promise<void> => {
            const pending = update(value);
            // The old private toggle waited for queryData; attendance/copy toggled immediately.
            if (wait) await pending;
            show(value);
            if (!wait) await pending;
        };
        const off = appendIconAction(parent, classes[0], icons[0], labels[0], () => change(1));
        const on = appendIconAction(parent, classes[1], icons[1], labels[1], () => change(0));
        off.id = ids[0];
        on.id = ids[1];
        show(state);
    };
    const active = document.createElement('div');
    active.className = 'd-inline-flex align-items-center gap-0';
    second.appendChild(active);
    pair(active, Number(row.player_character_inactive ?? 0),
        [`btn_toggle_character_active_${id}`, `btn_toggle_character_inactive_${id}`],
        ['player_character_active', 'player_character_inactive'], ['btn btn-sm btn-primary', 'btn btn-sm btn-danger'],
        ['Make character inactive', 'Make character active'], value => {
            openSideLog();
            return queryData(resolveUrl('options/tools/player_characters/update/update_player_character_inactive_inc.php'),
                'div_sidelog_warning', { item_id: id, player_character_inactive: value, csrf_token: csrfToken });
        });
    pair(second, Number(row.player_character_allow_copying ?? 0),
        [`btn_toggle_pc_allow_copy_list_${id}`, `btn_toggle_pc_disallow_copy_list_${id}`],
        ['share_player_character_disallowed', 'share_player_character_allowed'], ['btn btn-sm btn-dark border', 'btn btn-sm btn-light border'],
        ['Allow character copying', 'Disallow character copying'], value => {
            openSideLog();
            return queryData(resolveUrl('options/tools/player_characters/update/update_player_character_allow_copying_inc.php'),
                'div_sidelog_warning', { item_id: id, player_character_allow_copying: value, csrf_token: csrfToken });
        });
    const last = document.createElement('div');
    last.className = 'd-inline-flex align-items-center gap-1 flex-wrap';
    second.appendChild(last);
    pair(last, Number(row.player_character_private ?? 0), [`player_character_public_${id}`, `player_character_private_${id}`],
        ['public', 'private'], ['btn btn-primary btn-sm', 'btn btn-danger btn-sm'], ['Make character private', 'Make character public'],
        value => queryData(resolveUrl('options/db_queries/update/update_privacy_inc.php'), 'div_sidelog_warning',
            { item_type: 'player_character', player_character_id: id, player_character_private: String(value), csrf_token: csrfToken }), true);
    appendIconAction(last, 'btn btn-sm btn-danger', 'delete', 'Delete character', async () => {
        if (window.confirm('Delete this player character?')) await actions.ftTryDeleteCreature22?.(id, true);
    });

    const hidden = document.createElement('p');
    hidden.id = `hidden_share_pc_${id}`;
    hidden.className = 'd-none mb-0';
    hidden.textContent = resolveUrl(`item.php?id=${encoded}`);
    root.appendChild(hidden);
    const response = document.createElement('div');
    response.id = `div_response_player_character_card_${id}`;
    root.appendChild(response);
    return root;
}

function characterRow(row: PlayerCharacterListRow, data: PlayerCharacterListResponse): TableRow {
    const id = row.encounter_creature_id ?? row.item_id;
    const name = plain(row.player_name);
    const slots = aggregate<PlayerCharacterClassSlot>(row.class_slots_json);
    const level = playerCharacterTotalLevel(slots);
    const creatureNumber = Number(row.creature_number ?? 0);
    const cells: Record<string, TableCell> = {
        picture: { segments: [{ kind: 'thumbnail', pictures: portraits(row, data.cdn_url), item_id: id, name, link: true,
            w: 90, creature_number: creatureNumber > 0 ? creatureNumber : null }], sort: name, text: '' },
        name: nameCell(name, `item.php?id=${encodeURIComponent(id)}`),
        level: { segments: [{ kind: 'badge', value: String(level), variant: 'dark', cls: 'h5 border' }], sort: level, text: String(level) },
        class: classCell(slots),
        settings: { segments: [{ kind: 'node', build: () => settingsNode(row, data.csrf_token) }], sort: '', text: '' },
    };
    if (data.group === 'campaign') {
        const campaign = plain(row.campaign_name);
        cells.campaign = campaign && row.campaign_id
            ? nameCell(campaign, `campaign.php?campaign_id=${encodeURIComponent(row.campaign_id)}`)
            : { segments: [{ kind: 'text', value: 'None', cls: 'fst-italic text-muted' }], sort: '', text: '' };
    }
    return { item_id: id, cells };
}

export function adoptPlayerCharacterList(data: PlayerCharacterListResponse): CatalogTableResponse {
    if (data.schema !== PLAYER_CHARACTER_LIST_SCHEMA || !Array.isArray(data.characters)) throw new Error('catalog-player-character-list-schema');
    return { columns: columns(data.group), rows: data.characters.map(row => characterRow(row, data)), total: data.characters.length };
}
